import { useMemo, useState } from 'react'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import type { ReadinessCategoryRow, ReadinessShiftRow, ReadinessWorklistRow, Severity } from '../api/types'
import { ExceptionDrawer } from '../components/ExceptionDrawer'
import { useAcknowledge, useReadiness } from '../hooks/useApi'
import { tokens } from '../theme'

const WINDOW_CHOICES = [
  { label: 'Next 4 h', value: 4 },
  { label: 'Next 12 h', value: 12 },
  { label: 'Next 24 h', value: 24 },
  { label: 'Next 3 days', value: 72 },
  { label: 'Next 7 days', value: 168 },
]

const gradeColour: Record<string, string> = {
  green: '#22C55E',
  amber: '#E09935',
  red: tokens.alert,
  none: tokens.textSec,
}

const sevColour: Record<Severity, string> = {
  critical: tokens.alert,
  high: '#E09935',
  warn: tokens.gold,
  info: tokens.textSec,
}

const categoryIcon: Record<string, string> = {
  skills: '⚙',
  parts: '🛠',
  certifications: '⎈',
  repeat: '↻',
  access: '⌂',
  safety: '⚠',
}

export default function Readiness() {
  const [windowHours, setWindowHours] = useState(24)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data, isLoading, isError, error } = useReadiness(windowHours)

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Supervisor pre-flight view
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Everything the supervisor can still influence before the van rolls — skills, parts, certifications, access risk, repeat visits, safety.
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={windowHours}
              onChange={(_, v) => v && setWindowHours(v)}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none' } }}
            >
              {WINDOW_CHOICES.map((w) => (
                <ToggleButton key={w.value} value={w.value}>
                  {w.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>

          {isLoading && <LinearProgress />}

          {isError && (
            <Alert severity="error" variant="outlined">
              Failed to load readiness: {error instanceof Error ? error.message : 'unknown error'}
            </Alert>
          )}

          {data && (
            <>
              <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                <Grid size={{ xs: 12, md: 5, lg: 4 }}>
                  <ReadinessHero
                    score={data.readiness_score}
                    grade={data.readiness_grade}
                    jobsInWindow={data.summary.jobs_in_window}
                    readyCount={data.summary.ready_count}
                    atRiskCount={data.summary.at_risk_count}
                    unassignedCount={data.summary.unassigned_count}
                    windowHours={windowHours}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 7, lg: 8 }}>
                  <Grid container spacing={1.5} sx={{ height: '100%', alignContent: 'stretch' }}>
                    {(data.categories ?? []).map((cat) => (
                      <Grid key={cat.key} size={{ xs: 6, sm: 4 }}>
                        <CategoryTile category={cat} />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>

              <Worklist rows={data.worklist ?? []} onSelect={setSelectedId} />

              <ShiftSummary rows={data.shift_summary ?? []} />
            </>
          )}
        </Stack>

      <ExceptionDrawer id={selectedId} onClose={() => setSelectedId(null)} />
    </Container>
  )
}

// ---------------------------------------------------------------------------
// Hero — score dial + counts + grade-coloured headline
// ---------------------------------------------------------------------------

function ReadinessHero({
  score,
  grade,
  jobsInWindow,
  readyCount,
  atRiskCount,
  unassignedCount,
  windowHours,
}: {
  score: number
  grade: string
  jobsInWindow: number
  readyCount: number
  atRiskCount: number
  unassignedCount: number
  windowHours: number
}) {
  const isNoData = (grade as string) === 'none'
  const colour = gradeColour[grade] ?? tokens.textSec
  const size = 148
  const stroke = 14
  const r = size / 2 - stroke / 2
  const cx = size / 2
  const cy = size / 2
  const circ = 2 * Math.PI * r
  // No-data: full grey ring (offset=0 with grey stroke visually = the same
  // as the background circle, so the arc doesn't over-emphasise). With data:
  // arc length proportional to the score.
  const offset = isNoData ? circ : circ * (1 - Math.max(0, Math.min(100, score)) / 100)

  const gradeLabel = isNoData
    ? 'No data'
    : grade === 'green'
      ? 'Green'
      : grade === 'amber'
        ? 'Amber'
        : 'Red'

  const statement = isNoData
    ? `No jobs scheduled in the next ${windowHours} h — nothing to grade. Pick a longer window or wait for the WFM adapter.`
    : grade === 'green'
      ? 'Ready to dispatch.'
      : grade === 'amber'
        ? 'Dispatch is at risk — review the worklist.'
        : 'Hold and resolve blockers before dispatch.'

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: `4px solid ${colour}`,
      }}
    >
      <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
        Readiness score
      </Typography>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mt: 0.5, flex: 1 }}>
        <Box sx={{ flexShrink: 0 }}>
          <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
            <circle cx={cx} cy={cy} r={r} fill="none" stroke={tokens.divider} strokeWidth={stroke} />
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={colour}
              strokeWidth={stroke}
              strokeDasharray={circ}
              strokeDashoffset={offset}
              transform={`rotate(-90 ${cx} ${cy})`}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 600ms ease' }}
            />
            <text
              x={cx}
              y={cy - 2}
              textAnchor="middle"
              fontSize={isNoData ? 30 : 34}
              fontWeight={800}
              fill={isNoData ? tokens.textSec : tokens.charcoal}
            >
              {isNoData ? '—' : score}
            </text>
            <text x={cx} y={cy + 16} textAnchor="middle" fontSize="9" fill={tokens.textSec}>
              {isNoData ? 'no data' : '/ 100'}
            </text>
          </svg>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: colour, fontSize: 22, lineHeight: 1.1 }}>
            {gradeLabel}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
            {statement}
          </Typography>
          <Stack spacing={0.25}>
            <Row label="Jobs in window" value={jobsInWindow} />
            <Row label="Ready to dispatch" value={readyCount} colour="#22886E" />
            <Row label="At-risk" value={atRiskCount} colour={tokens.alert} />
            {unassignedCount > 0 && <Row label="Unassigned" value={unassignedCount} colour={tokens.indigoLight} />}
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
            Window: next {windowHours} h
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}

function Row({ label, value, colour }: { label: string; value: number; colour?: string }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: colour ?? 'text.primary' }}>
        {value.toLocaleString()}
      </Typography>
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// Category tile — 6 of them. Click the tile to anchor / jump to worklist.
// ---------------------------------------------------------------------------

function CategoryTile({ category }: { category: ReadinessCategoryRow }) {
  const colour = gradeColour[category.status] ?? tokens.textSec
  const emphasise = category.at_risk_count > 0
  return (
    <Tooltip title={category.description}>
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          height: '100%',
          borderTop: `3px solid ${colour}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          minHeight: 118,
        }}
      >
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          <Typography sx={{ fontSize: 16, color: colour, lineHeight: 1 }}>{categoryIcon[category.key] ?? '●'}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }} noWrap>
            {category.label}
          </Typography>
          <Chip
            size="small"
            label={category.status.toUpperCase()}
            sx={{
              height: 18,
              fontSize: 9,
              fontWeight: 700,
              bgcolor: `${colour}22`,
              color: colour,
              flexShrink: 0,
            }}
          />
        </Stack>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <Typography
            sx={{
              fontWeight: 700,
              lineHeight: 1.05,
              fontSize: 26,
              fontVariantNumeric: 'tabular-nums',
              color: emphasise ? colour : tokens.charcoal,
            }}
          >
            {category.at_risk_count}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {category.at_risk_count === 0
              ? 'Clear'
              : `at-risk${category.critical_count > 0 ? ` · ${category.critical_count} critical` : ''}`}
          </Typography>
        </Box>
      </Paper>
    </Tooltip>
  )
}

// ---------------------------------------------------------------------------
// Worklist — every pre-flight blocker in the window, prioritised
// ---------------------------------------------------------------------------

function Worklist({ rows, onSelect }: { rows: ReadinessWorklistRow[]; onSelect: (id: string) => void }) {
  const ack = useAcknowledge()
  return (
    <Paper variant="outlined">
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Pre-flight worklist
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Critical + high first, then the rest by dispatch time. Acknowledge clears the supervisor's queue; resolve stops the rule from re-raising.
        </Typography>
      </Box>
      {rows.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No pre-flight blockers in the window. You're cleared to dispatch.
          </Typography>
        </Box>
      ) : (
        <Stack divider={<Divider />}>
          {rows.map((row) => (
            <WorklistRow
              key={row.exception_id}
              row={row}
              onOpen={() => onSelect(row.exception_id)}
              onAck={() => ack.mutate(row.exception_id)}
              ackPending={ack.isPending}
            />
          ))}
        </Stack>
      )}
    </Paper>
  )
}

function WorklistRow({
  row,
  onOpen,
  onAck,
  ackPending,
}: {
  row: ReadinessWorklistRow
  onOpen: () => void
  onAck: () => void
  ackPending: boolean
}) {
  const colour = sevColour[row.severity as Severity]
  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5, p: 1.5, '&:hover': { bgcolor: 'action.hover' } }}>
      {/* severity pip */}
      <Box sx={{ width: 6, bgcolor: colour, borderRadius: 1, flexShrink: 0 }} aria-label={`severity ${row.severity}`} />

      {/* content column — takes remaining space, wraps freely */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
          <Chip
            size="small"
            label={row.severity}
            sx={{
              height: 18,
              fontSize: 9,
              fontWeight: 700,
              textTransform: 'uppercase',
              bgcolor: colour,
              color: 'common.white',
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 12 }}>
            {row.code}
          </Typography>
          <Chip
            size="small"
            label={row.category}
            variant="outlined"
            sx={{ height: 18, fontSize: 10, textTransform: 'capitalize' }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
            {row.subject_summary || row.subject_type}
          </Typography>
          {row.engineer_name && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              · {row.engineer_name}
            </Typography>
          )}
          {row.scheduled_start && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              · dispatch {formatDispatchTime(row.scheduled_start)}
            </Typography>
          )}
        </Stack>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {row.suggested_action || '—'}
        </Typography>
      </Box>

      {/* action column — fixed width so it never overlaps content */}
      <Stack direction="column" spacing={0.5} sx={{ flexShrink: 0, width: 100, justifyContent: 'center' }}>
        <Button
          size="small"
          variant="contained"
          disabled={ackPending}
          onClick={onAck}
          sx={{ textTransform: 'none' }}
        >
          Ack
        </Button>
        <Button size="small" variant="outlined" onClick={onOpen} sx={{ textTransform: 'none' }}>
          Open
        </Button>
      </Stack>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Shift summary — per-engineer roll-up
// ---------------------------------------------------------------------------

function ShiftSummary({ rows }: { rows: ReadinessShiftRow[] }) {
  const readyEngineers = useMemo(() => rows.filter((r) => r.jobs_at_risk === 0).length, [rows])
  return (
    <Paper variant="outlined">
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Shift summary — engineers on this window
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {rows.length} engineer{rows.length === 1 ? '' : 's'} scheduled
          </Typography>
          {readyEngineers > 0 && (
            <Chip size="small" label={`${readyEngineers} fully ready`} sx={{ bgcolor: '#E8F5E9', color: '#1B5E20', height: 20 }} />
          )}
        </Stack>
      </Box>
      {rows.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No engineers scheduled in this window.
          </Typography>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Engineer</TableCell>
              <TableCell>Ref</TableCell>
              <TableCell>State</TableCell>
              <TableCell align="right">Jobs</TableCell>
              <TableCell align="right">At-risk</TableCell>
              <TableCell>Top blocker</TableCell>
              <TableCell>Readiness</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const readyPct = r.jobs_scheduled === 0 ? 100 : Math.round(((r.jobs_scheduled - r.jobs_at_risk) / r.jobs_scheduled) * 100)
              const colour = readyPct >= 90 ? '#22C55E' : readyPct >= 70 ? '#E09935' : tokens.alert
              return (
                <TableRow key={r.engineer_id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{r.display_name}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{r.external_ref}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.state} variant="outlined" sx={{ height: 18, fontSize: 10, textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell align="right">{r.jobs_scheduled}</TableCell>
                  <TableCell align="right" sx={{ color: r.jobs_at_risk > 0 ? tokens.alert : 'text.primary', fontWeight: r.jobs_at_risk > 0 ? 700 : 400 }}>
                    {r.jobs_at_risk}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
                    {r.top_blocker || '—'}
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Box sx={{ flex: 1, height: 6, bgcolor: tokens.divider, borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ width: `${readyPct}%`, height: '100%', bgcolor: colour }} />
                      </Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: colour, minWidth: 36, textAlign: 'right' }}>
                        {readyPct}%
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </Paper>
  )
}

function formatDispatchTime(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const diffMin = (d.getTime() - now.getTime()) / 60_000
  const timeStr = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  if (diffMin < 0) return `${timeStr} (now)`
  if (diffMin < 60) return `${timeStr} (${Math.round(diffMin)}m)`
  if (diffMin < 60 * 24) return `${timeStr} (${Math.round(diffMin / 60)}h)`
  return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} ${timeStr}`
}
